<script setup>
/* eslint-disable */
import { useGameStore } from '@/store/game'
import GameSetup from '@/components/GameSetup.vue'
import MainGame from '@/components/MainGame.vue'
import HowToPlay from '@/components/HowTo.vue'
import { ref } from 'vue'
import logo from '@/assets/logo.png'

const gameStore = useGameStore()
const showSetup = ref(false)
const showDeleteConfirm = ref(false)
const showHowToPlay = ref(false)
const loadError = ref(false)

const hasSavedGame = ref(!!localStorage.getItem('chocobo-manager-save'))

const startNewGame = () => {
  if (hasSavedGame.value) {
    showDeleteConfirm.value = true
  } else {
    showSetup.value = true
  }
}

const continueGame = () => {
  if (hasSavedGame.value) {
    try {
      const savedGame = gameStore.loadGame()
      if (savedGame) {
        gameStore.restoreGame(savedGame)
      } else {
        loadError.value = true
      }
    } catch (error) {
      console.error('Failed to load saved game:', error)
      loadError.value = true
    }
  }
}

const confirmNewGame = () => {
  localStorage.removeItem('chocobo-manager-save')
  hasSavedGame.value = false
  showDeleteConfirm.value = false
  showSetup.value = true
}

const cancelNewGame = () => {
  showDeleteConfirm.value = false
}

const showHowToPlayModal = () => {
  showHowToPlay.value = true
}

const closeHowToPlayModal = () => {
  showHowToPlay.value = false
}

const dismissLoadError = () => {
  loadError.value = false
}
</script>

<template>
  <div class="min-h-screen bg-gray-100 flex flex-col">
    <div v-if="!gameStore.isLoaded && !showSetup" class="flex-1 flex items-center justify-center">
      <div class="max-w-md w-full">
        <img :src="logo" alt="Chocobo Racing Manager" class="mx-auto mb-8 h-auto w-auto">
        
        <div class="space-y-4">
          <!-- Continue Game Button -->
          <button 
            v-if="hasSavedGame"
            @click="continueGame"
            class="w-full px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
          >
            Continue Game
          </button>

          <!-- New Game Button -->
          <button 
            @click="startNewGame"
            class="w-full px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
          >
            {{ hasSavedGame ? 'New Game' : 'Start New Game' }}
          </button>

          <!-- How to Play Button -->
          <button 
            @click="showHowToPlayModal"
            class="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
          >
            How to Play
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Save Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-xl font-bold mb-4">Delete Existing Save?</h3>
        <p class="text-gray-600 mb-6">
          Starting a new game will permanently delete your existing save data. This cannot be undone. Are you sure you want to continue?
        </p>
        <div class="flex justify-end space-x-4">
          <button 
            @click="cancelNewGame"
            class="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            @click="confirmNewGame"
            class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Delete Save & Start New Game
          </button>
        </div>
      </div>
    </div>

    <!-- Load Error Modal -->
    <div v-if="loadError" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 class="text-xl font-bold mb-4">Unable to Load Save</h3>
        <p class="text-gray-600 mb-6">
          There was an error loading your saved game. The save file might be corrupted or incompatible with the current version.
        </p>
        <div class="flex justify-end">
          <button 
            @click="dismissLoadError"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            OK
          </button>
        </div>
      </div>
    </div>

    <!-- How to Play Modal -->
    <HowToPlay v-if="showHowToPlay" @close="closeHowToPlayModal" />

    <!-- Main Game Content -->
    <div class="flex-1">
      <GameSetup v-if="showSetup && !gameStore.isLoaded" />
      <MainGame v-if="gameStore.isLoaded" />
    </div>
  </div>
</template>